/**
 * RunsSidebar — paginated list of recent runs.
 *
 *   - Header: "History" heading + Refresh button.
 *   - List: 10 RunCards per page (page-replace, not append).
 *   - Footer: Mantine Pagination control — only shown when >1 page exists.
 *
 * Writers see their own runs; admin/system_owner see all writers' runs.
 */
import { Group, Pagination, ScrollArea, Stack, Text } from '@mantine/core';
import { ActionIcon, Tooltip } from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import { useRuns } from '../../hooks/use-runs';
import { RunCard } from './RunCard';
import { SectionHeading } from './SectionHeading';

export function RunsSidebar() {
  const { runs, page, totalPages, loading, refetch, goToPage } = useRuns();

  return (
    <Stack gap="sm" data-testid="runs-sidebar">
      <Group justify="space-between" align="center" wrap="nowrap">
        <SectionHeading>History</SectionHeading>

        <Tooltip label="Refresh" withArrow>
          <ActionIcon
            variant="default"
            size="md"
            data-testid="refresh-btn"
            onClick={refetch}
            loading={loading}
            aria-label="Refresh runs list"
          >
            <IconRefresh size={16} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <ScrollArea>
        <Stack gap="xs" data-testid="runs-list">
          {runs.length === 0 && !loading ? (
            <Text c="dimmed" fz="sm">
              No runs yet.
            </Text>
          ) : (
            runs.map((run) => <RunCard key={run.id} run={run} />)
          )}
        </Stack>
      </ScrollArea>

      {totalPages > 1 && (
        <Group justify="center" mt="xs">
          <Pagination
            total={totalPages}
            value={page}
            onChange={goToPage}
            size="sm"
            withEdges
            data-testid="runs-pagination"
          />
        </Group>
      )}
    </Stack>
  );
}
